import { sortPostStyles } from "@/lib/post-style-order";

interface StudioWritingStyleOptionsProps {
  request: string;
  studioPostStyle: string | undefined;
  setStudioPostStyle: (v: string | undefined) => void;
  studioHookStyle: string | undefined;
  setStudioHookStyle: (v: string | undefined) => void;
  studioSpacingStyle: string | undefined;
  setStudioSpacingStyle: (v: string | undefined) => void;
  studioPerspective: string | undefined;
  setStudioPerspective: (v: string | undefined) => void;
  studioHumorLevel: string | undefined;
  setStudioHumorLevel: (v: string | undefined) => void;
  studioAudience: string | undefined;
  setStudioAudience: (v: string | undefined) => void;
  studioEmojiUsage: string | undefined;
  setStudioEmojiUsage: (v: string | undefined) => void;
}

export function StudioWritingStyleOptions({
  request,
  studioPostStyle,
  setStudioPostStyle,
  studioHookStyle,
  setStudioHookStyle,
  studioSpacingStyle,
  setStudioSpacingStyle,
  studioPerspective,
  setStudioPerspective,
  studioHumorLevel,
  setStudioHumorLevel,
  studioAudience,
  setStudioAudience,
  studioEmojiUsage,
  setStudioEmojiUsage,
}: StudioWritingStyleOptionsProps) {
  return (
    <>
      {/* Content Style */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Content Style</label>
          {studioPostStyle && <button onClick={() => setStudioPostStyle(undefined)} className="text-[10px] text-muted-foreground hover:text-foreground underline transition-colors">Clear</button>}
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          {sortPostStyles([
            { value: "storytelling", label: "Storytelling", desc: "Narrative arc" },
            { value: "factual",      label: "Factual",      desc: "Data-driven" },
            { value: "how-to",       label: "How-To",       desc: "Step-by-step" },
            { value: "personal",     label: "Personal",     desc: "Introspective" },
          ], request).map((s) => (
            <button
              key={s.value}
              onClick={() => setStudioPostStyle(studioPostStyle === s.value ? undefined : s.value)}
              className={`flex flex-col items-start px-3 py-2 rounded-xl text-left border transition-all ${
                studioPostStyle === s.value
                  ? "bg-primary/10 border-primary/40 text-primary"
                  : "bg-background border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"
              }`}
            >
              <span className="text-xs font-semibold">{s.label}</span>
              <span className="text-[10px] opacity-70">{s.desc}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Hook Style */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Opening Hook</label>
          {studioHookStyle && <button onClick={() => setStudioHookStyle(undefined)} className="text-[10px] text-muted-foreground hover:text-foreground underline transition-colors">Clear</button>}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {[
            { value: "bold-statement", label: "Bold statement" },
            { value: "question",       label: "Question" },
            { value: "statistic",      label: "Statistic" },
            { value: "anecdote",       label: "Anecdote" },
          ].map((h) => (
            <button
              key={h.value}
              onClick={() => setStudioHookStyle(studioHookStyle === h.value ? undefined : h.value)}
              className={`px-3 py-1.5 rounded-xl text-xs font-medium border transition-all ${
                studioHookStyle === h.value
                  ? "bg-primary/10 border-primary/40 text-primary"
                  : "bg-background border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"
              }`}
            >
              {h.label}
            </button>
          ))}
        </div>
      </div>

      {/* Spacing */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Spacing</label>
          {studioSpacingStyle && <button onClick={() => setStudioSpacingStyle(undefined)} className="text-[10px] text-muted-foreground hover:text-foreground underline transition-colors">Clear</button>}
        </div>
        <div className="flex gap-1.5">
          {[
            { value: "tight",  label: "Tight",  desc: "Dense paragraphs" },
            { value: "airy",   label: "Airy",   desc: "Room to breathe" },
            { value: "punchy", label: "Punchy", desc: "One thought/line" },
          ].map((s) => (
            <button
              key={s.value}
              onClick={() => setStudioSpacingStyle(studioSpacingStyle === s.value ? undefined : s.value)}
              className={`flex-1 flex flex-col items-center px-2 py-2 rounded-xl text-center border transition-all ${
                studioSpacingStyle === s.value
                  ? "bg-primary/10 border-primary/40 text-primary"
                  : "bg-background border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"
              }`}
            >
              <span className="text-xs font-semibold">{s.label}</span>
              <span className="text-[10px] opacity-60 mt-0.5">{s.desc}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Perspective */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Perspective</label>
          {studioPerspective && <button onClick={() => setStudioPerspective(undefined)} className="text-[10px] text-muted-foreground hover:text-foreground underline transition-colors">Clear</button>}
        </div>
        <div className="flex gap-1.5">
          {[
            { value: "first-person",  label: "I / Me",  desc: "Personal" },
            { value: "second-person", label: "You",     desc: "Direct to reader" },
            { value: "collective",    label: "We / Us", desc: "Community" },
          ].map((p) => (
            <button
              key={p.value}
              onClick={() => setStudioPerspective(studioPerspective === p.value ? undefined : p.value)}
              className={`flex-1 flex flex-col items-center px-2 py-2 rounded-xl text-center border transition-all ${
                studioPerspective === p.value
                  ? "bg-primary/10 border-primary/40 text-primary"
                  : "bg-background border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"
              }`}
            >
              <span className="text-xs font-semibold">{p.label}</span>
              <span className="text-[10px] opacity-60 mt-0.5">{p.desc}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Humor Level */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Humor</label>
          {studioHumorLevel && <button onClick={() => setStudioHumorLevel(undefined)} className="text-[10px] text-muted-foreground hover:text-foreground underline transition-colors">Clear</button>}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {[
            { value: "none",    label: "None",    desc: "Serious" },
            { value: "dry-wit", label: "Dry wit", desc: "Deadpan" },
            { value: "witty",   label: "Witty",   desc: "Clever" },
            { value: "playful", label: "Playful", desc: "Fun" },
          ].map((h) => (
            <button
              key={h.value}
              onClick={() => setStudioHumorLevel(studioHumorLevel === h.value ? undefined : h.value)}
              className={`flex-1 flex flex-col items-center px-2 py-2 rounded-xl text-center border transition-all ${
                studioHumorLevel === h.value
                  ? "bg-primary/10 border-primary/40 text-primary"
                  : "bg-background border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"
              }`}
            >
              <span className="text-xs font-semibold">{h.label}</span>
              <span className="text-[10px] opacity-60 mt-0.5">{h.desc}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Audience */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Target Audience</label>
          {studioAudience && <button onClick={() => setStudioAudience(undefined)} className="text-[10px] text-muted-foreground hover:text-foreground underline transition-colors">Clear</button>}
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          {[
            { value: "general",      label: "General",      desc: "Everyone" },
            { value: "professional", label: "Professional", desc: "Peers / industry" },
            { value: "executive",    label: "Executive",    desc: "C-suite / founders" },
            { value: "friends",      label: "Community",    desc: "Close followers" },
          ].map((a) => (
            <button
              key={a.value}
              onClick={() => setStudioAudience(studioAudience === a.value ? undefined : a.value)}
              className={`flex flex-col items-start px-3 py-2 rounded-xl text-left border transition-all ${
                studioAudience === a.value
                  ? "bg-primary/10 border-primary/40 text-primary"
                  : "bg-background border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"
              }`}
            >
              <span className="text-xs font-semibold">{a.label}</span>
              <span className="text-[10px] opacity-70">{a.desc}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Emoji Usage */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Emoji Usage</label>
          {studioEmojiUsage && <button onClick={() => setStudioEmojiUsage(undefined)} className="text-[10px] text-muted-foreground hover:text-foreground underline transition-colors">Clear</button>}
        </div>
        <div className="flex gap-1.5">
          {[
            { value: "off",        label: "Off",        desc: "Zero" },
            { value: "minimal",    label: "Minimal",    desc: "1–2" },
            { value: "natural",    label: "Natural",    desc: "As needed" },
            { value: "expressive", label: "Expressive", desc: "Frequent" },
          ].map((e) => (
            <button
              key={e.value}
              onClick={() => setStudioEmojiUsage(studioEmojiUsage === e.value ? undefined : e.value)}
              className={`flex-1 flex flex-col items-center px-2 py-2 rounded-xl text-center border transition-all ${
                studioEmojiUsage === e.value
                  ? "bg-primary/10 border-primary/40 text-primary"
                  : "bg-background border-border text-muted-foreground hover:border-primary/30 hover:text-foreground"
              }`}
            >
              <span className="text-xs font-semibold">{e.label}</span>
              <span className="text-[10px] opacity-60 mt-0.5">{e.desc}</span>
            </button>
          ))}
        </div>
      </div>
    </>
  );
}
